/**
 * 数据源类代表了若干地图要素的集合
 * 其中通常会按照瓦片和要素类型来组织数据,可能还包含有各种类型的索引,
 * 该类提供各种对数据查询的方法.
 */
export default class Source {
    /**
     * 数据源对象的初始化方法.
     * @param {String} name - 数据源名称，系统中可选值包括：
     * objSource要素数据源名称；
     * tipSource tips数据原名称；
     * thematicSource 专题图数据源名称
     * @param {Object} config - 数据原配置文件中的定义数据
     * @returns {undefined}
     */
    constructor(name, config) {
        /**
         * 数据源名称
         * @type {String}
         */
        this.name = name;
        /**
         * 以瓦片为单位，进行要素数据的组织
         * @type {Object}
         */
        this._data = {};
    }

    /**
     * 获取数据
     * @return {object} 数据
     */
    getData() {
        return this._data;
    }


    /**
     * 给数据源中增加具体的要素数据.
     * @param {Object} feature - 要素渲染模型
     * @param {Object} tile    - 瓦片对象
     * @returns {undefined}
     */
    add(feature, tile) {
        this._data[tile] = feature;
    }

    /**
     * 将要素从瓦片数据对象中删除
     * @param {Object} feature  要素模型
     * @param {Object} tileData 瓦片数据对象 
     */
    _delFrom(feature, tileData) {
        const id = feature.id;
        const type = feature.type;
        if (!tileData || !tileData[type] || !tileData[type].index[id]) {
            return;
        }

        const posArray = tileData[type].index[id].sort((a, b) => a - b);
        const features = tileData[type].features;
        for (let i = 0; i < posArray.length; ++i) {
            const pos = posArray[i];
            features.splice(pos, 1);
        }
        delete tileData[type].index[id];
    }

    /**
     * 删除特定瓦片下的指定要素.
     * @param {Object} feature - 要素渲染模型
     * @param {Object} tileName - 瓦片的名称
     * @returns {undefined} - 返回值为undefined
     */
    del(feature, tileName) {
        if (!feature) {
            throw new Error('feature参数缺失');
        }
        if (!tileName) {
            throw new Error('tileName参数缺失');
        }
        if (!feature.properties) {
            throw new Error('feature缺少properties属性');
        }

        const type = feature.type;
        if (!type) {
            throw new Error('feature缺少type属性');
        }

        if (!this._data.hasOwnProperty(tileName)) {
            return;
        }

        if (!this._data[tileName].hasOwnProperty(type)) {
            return;
        }

        const id = feature.id;
        if (!id) {
            throw new Error('feature缺少id属性');
        }

        this._delFrom(feature, this._data[tileName]);

        if (feature.isShifted) {
            this._delFrom(feature, this._data[tileName]);
        }
    }

    /**
     * 删除某个瓦片下的所有数据.
     * @param {String} tileName - 瓦片的名字
     * @returns {undefined}
     */
    delByTile(tileName) {
        if (this._data.hasOwnProperty(tileName)) {
            delete this._data[tileName];
        }
        // if (this._shiftedData.hasOwnProperty(tileName)) {
        //     delete this._shiftedData[tileName];
        // }
    }

    /**
     * 删除指定瓦片下的给定要素数据.
     * @param {String} geoLiveType - 具体要素的数据类型
     * @param {String} tileName - 瓦片的名字例如："1725583:794203:21"
     * @returns {undefined}
     */
    delByType(type, tileName) {
        if (this._data.hasOwnProperty(tileName) && this._data[tileName].hasOwnProperty(type)) {
            delete this._data[tileName][type];
        }

        if (this._shiftedData.hasOwnProperty(tileName) && this._shiftedData[tileName].hasOwnProperty(type)) {
            delete this._shiftedData[tileName][type];
        }
    }

    /**
     * 清空当前数据源.
     * @returns {undefined}
     */
    clear() {
        this._data = {};
        this._shiftedData = {};
    }
  /**
   * 根据特定要素的id，获取要素.
   * @param {String} id - 要素中唯一标识
   * @param {Number} scenes - 在哪个场景寻找要素
   * @param {Number} type - 要素类型
   * @param {Number} tileName - 瓦片名
   * @param {Number} fieId - 要素中某个字段
   * @returns {Array} tileNames
   */
  queryNodeByTile(id, scenes, type, tileName, fieId) {
    const object3D = scenes[type].tiles[tileName];

    const result = [];
    object3D.traverseVisible((item) => {
      if (item.prop && item.prop[fieId] === id) {
        result.push(item);
      }
    });
    return result;
  }

  /**
   * 根据特定要素的id，获取要素.
   * @param {String} fieId - 要素中某个字段
   * @param {Number} id - 要素的唯一标识
   * @returns {Array} tileNames
   */
  queryTileByFieId(id, fieId) {
    if (!fieId) {
      return null;
    }
    const { _data } = this;
    const keys = Object.getOwnPropertyNames(this._data);
    for (let i = 0; i < keys.length; i++) {
      const tileName = keys[i];
      for (let j = 0; j < _data[tileName].length; j++) {
        if (_data[tileName][j][fieId] === id) {
          return tileName;
        }
      }
    }
    return null;
  }
    /**
     * 根据特定要素的id，获得要素所在的瓦片信息.
     * @param {String} type - 要素类型
     * @param {Number} id - 要素的唯一标识
     * @returns {Array} tileNames
     */
    queryTilesById(type, id) {
        if (!type || !id) {
            return [];
        }

        const tileNames = [];
        const keys = Object.getOwnPropertyNames(this._data);
        for (let i = 0; i < keys.length; ++i) {
            const tileName = keys[i];
            if (!this._data[tileName].hasOwnProperty(type)) {
                continue;
            }
            if (!this._data[tileName][type].index.hasOwnProperty(id)) {
                continue;
            }
            tileNames.push(tileName);
        }

        return tileNames;
    }

    /**
     * 在指定瓦片范围内获得根据某一要素的id获得其渲染数据模型.
     * @param {String} type - 要素类型
     * @param {Number} id - 要素的唯一标识
     * @param {String} tileName - 瓦片的名字例如："1725583:794203:21"
     * @returns {Object} feature - 该要素的渲染模型
     */
    queryFeatureById(type, id, tileName) {
        if (!this._data.hasOwnProperty(tileName)) {
            return null;
        }

        if (!this._data[tileName].hasOwnProperty(type)) {
            return null;
        }

        if (!this._data[tileName][type].index.hasOwnProperty(id)) {
            return null;
        }

        const features = this._data[tileName][type].features;
        const posArray = this._data[tileName][type].index[id];
        const feature = features[posArray[0]];

        return feature;
    }

    /**
     * 获取某一瓦片下一种特定类型的所有数据.
     * @param {String} type - 要素类型
     * @param {String} tileName - 瓦片的名字
     * @returns {Array} features
     */
    queryFeaturesByType(type, tileName) {
        if (!this._data.hasOwnProperty(tileName)) {
            return [];
        }

        if (!this._data[tileName].hasOwnProperty(type)) {
            return [];
        }

        const features = this._data[tileName][type].features;
        return features;
    }

    /**
     * 获取特定类型的偏移所有数据.
     * @param {String} type - 要素类型
     * @returns {Array} features
     */
    queryShiftedFeaturesByType(type) {
        let features = [];
        for (const key in this._shiftedData) {
            if (this._shiftedData.hasOwnProperty(key)) {
                const element = this._shiftedData[key];
                if (element.hasOwnProperty(type)) {
                    features = [...features, ...element[type].features];
                }
            }
        }
        return features;
    }

    /**
     * 获得当前加载的所有瓦片信息.
     * @returns {Array} - 以数组的方式返回当前数据源所在的所有瓦片的名称
     */
    queryTiles() {
        return Object.getOwnPropertyNames(this._data);
    }

    /**
     * 分配请求子域名.
     * @param {Object} tile - 请求瓦片
     * @returns {Number} - 子域名具体值
     */
    _getSubdomain(tile) {
        if (this._subdomains.length === 0) {
            return null;
        }

        // 将瓦片在整个世界坐标系中的位置转成一维数组再取模
        // const index = (tile.x + Math.pow(2, 2 * tile.z) * tile.y + 1) % this._subdomains.length; // 19以上返回的大部分是0

        const index = (tile.x + tile.z + tile.y) % this._subdomains.length;

        return this._subdomains[index];
    }

    /**
     * 判断瓦片是否需要加载数据
     * 可以在config配置中进行重写覆盖
     * @param {Object} tile 瓦片对象
     * @param {Object} placeholders 占位符参数
     */
    isTileValid(tile, placeholders = this._placeholders) {
        return true;
    }

    /**
     * 按固定长度切割数组
     * @param {Array} arr 数组
     * @param {Number} size 切割长度
     * @returns {Array} 切割后的数据 [[],[],[]]
     */
    _chunk(arr, size) {
        const t = [];
        for (let i = 0; i < arr.length; i += size) {
            t.push(arr.slice(i, i + size));
        }
        return t;
    }
}
